import { MediaCitation } from "../types";

// // // //

/**
 * The single entity every citation entry points at. Defined once inside the
 * CollectionPage and referenced by `@id` everywhere else, so crawlers read the
 * citations as one aggregate signal about one product rather than N loosely
 * similar things.
 */
export const RWA_SOFTWARE_APPLICATION_ID_SUFFIX = "#software";

/**
 * PDF citations are institutional reports (that's how the page itself splits
 * them - see the "Institutional Report" / "Media" filter); everything else is
 * press coverage.
 */
function getCitationType(citation: MediaCitation): "Report" | "NewsArticle" {
    return citation.is_pdf ? "Report" : "NewsArticle";
}

/**
 * schema.org `datePublished` wants a date, not a timestamp. Returns null for
 * missing or unparseable values so the property can be dropped entirely
 * instead of emitting an invalid one.
 */
function getDatePublished(publishedDate: string): string | null {
    if (!publishedDate) {
        return null;
    }

    const parsed = new Date(publishedDate);

    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return parsed.toISOString().slice(0, 10);
}

// // // //

/**
 * getMediaCitationsJsonLd
 * Builds the CollectionPage JSON-LD for the /citations page - one entry per
 * citation, each linked back to the RWA.xyz product entity.
 *
 * @param {MediaCitation[]} props.citations - The citations rendered on the page.
 * @param {string} props.pageUrl - Absolute url of the citations page.
 * @param {string} props.productUrl - Absolute url of the product the citations reference.
 * @param {string} props.name - Page title.
 * @param {string} props.description - Page description.
 */
export function getMediaCitationsJsonLd(props: {
    citations: MediaCitation[];
    pageUrl: string;
    productUrl: string;
    name: string;
    description: string;
}): Record<string, unknown> {
    const { citations, pageUrl, productUrl, name, description } = props;

    const productID = `${productUrl}${RWA_SOFTWARE_APPLICATION_ID_SUFFIX}`;

    const hasPart = citations
        // A citation without a source url can't be verified by a crawler, and
        // one without a title has nothing to identify it - skip both
        .filter((citation) => Boolean(citation.url) && Boolean(citation.title))
        .map((citation) => {
            const publisher: Record<string, unknown> = {
                "@type": "Organization",
                name: citation.publisher.name,
            };

            if (citation.publisher.website) {
                publisher.url = citation.publisher.website;
            }

            const datePublished = getDatePublished(citation.published_date);

            return {
                "@type": getCitationType(citation),
                name: citation.title,
                // NOTE - the canonical source url, never a copy hosted by us.
                // This is what lets a crawler verify the citation is real.
                url: citation.url,
                author: publisher,
                publisher,
                ...(datePublished ? { datePublished } : {}),
                citation: { "@id": productID },
            };
        });

    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": pageUrl,
        name,
        description,
        url: pageUrl,
        about: {
            "@type": "SoftwareApplication",
            "@id": productID,
            name: "RWA.xyz",
            url: productUrl,
            applicationCategory: "BusinessApplication",
        },
        hasPart,
    };
}
